import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import type { StorefrontThemeSettings } from "../../app/models/StorefrontThemeSettings";
import type { UserSetting } from "../../app/models/UserSetting";
import type {
    StorefrontRenderingMode,
    StorefrontResultsSettings,
    StorefrontUiSettings,
} from "../../app/models/StorefrontResultsSettings";
import { useStore } from "../../app/stores/store";
import type { AppearanceNumberDraft } from "./AppearanceNumberField";
import { mergeResultsAppearance, type AppearanceNumberDrafts } from "./ResultsAppearanceControls";
import {
    DEFAULT_THEME_SETTINGS,
    type ResultsPageLocationMode,
    type SearchDestinationMode,
    cloneResultsSettings,
    resultSettingsSnapshot,
    stripVehiclePlaceholderFromPath,
    themeSettingsSnapshot,
    withResultsDefaults,
    withUiDefaults,
} from "./storefrontSettingsModel";
import useFitmentTagSync from "./useFitmentTagSync";

/**
 * Drafts of the storefront theme and results settings, shared by the Search &
 * results, Appearance and Advanced settings pages. Components using it must be
 * MobX observers, since the saved theme and user settings come from the store.
 */
export default function useStorefrontSettings() {
    const { settingStore } = useStore();
    const { currentSettings: settings, themeSettings, loadingInitial, loadingThemeSettings } = settingStore;
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    const [persistentFilteringChecked, setPersistentFilteringChecked] = useState(false);
    const [submodelEnabled, setSubmodelEnabled] = useState(false);
    const [themeDraft, setThemeDraft] = useState<StorefrontThemeSettings>();
    const themeDraftAfterSync = useRef<Partial<StorefrontThemeSettings> | undefined>(undefined);
    const [resultsLoading, setResultsLoading] = useState(true);
    const [resultsDraft, setResultsDraft] = useState<StorefrontResultsSettings>();
    const [resultsBaseline, setResultsBaseline] = useState<StorefrontResultsSettings>();
    const [appearanceNumberDrafts, setAppearanceNumberDrafts] = useState<AppearanceNumberDrafts>({});

    useEffect(() => {
        void settingStore.loadSettings();
        void settingStore.loadThemeSettings();
    }, [settingStore]);

    useEffect(() => {
        if (!settings) return;
        setPersistentFilteringChecked(!!settings.searchEnablePersistentFiltering);
        setSubmodelEnabled(settings.submodelEnabled ?? false);
    }, [settings]);

    useEffect(() => {
        if (!themeSettings) return;
        setThemeDraft({ ...themeSettings, ...themeDraftAfterSync.current });
        themeDraftAfterSync.current = undefined;
    }, [themeSettings]);

    useEffect(() => {
        let active = true;
        setResultsLoading(true);
        agent.Settings.storefrontResults()
            .then(loaded => {
                if (!active) return;
                const next = cloneResultsSettings(withResultsDefaults(loaded));
                setResultsDraft(next);
                setResultsBaseline(cloneResultsSettings(next));
            })
            .catch((err: any) => { if (active) setError(err?.message ?? "Failed to load storefront settings."); })
            .finally(() => { if (active) setResultsLoading(false); });
        return () => { active = false; };
    }, []);

    const updateThemeDraft = useCallback((key: keyof StorefrontThemeSettings, value: string | boolean | number) => {
        setThemeDraft(current => current ? ({ ...current, [key]: value } as StorefrontThemeSettings) : current);
    }, []);

    const updateSearchDestinationMode = useCallback((mode: SearchDestinationMode) => {
        setThemeDraft(current => current && { ...current, redirectToResults: mode === "ResultsPage" });
    }, []);

    const updateResultsPageLocationMode = useCallback((mode: ResultsPageLocationMode) => {
        setThemeDraft(current => current && { ...current, customRedirectEnabled: mode === "Custom" });
    }, []);

    const updateResultsDraft = useCallback((changes: Partial<StorefrontResultsSettings>) => {
        setResultsDraft(current => current ? ({ ...current, ...changes }) : current);
    }, []);

    const updateUiSettingsDraft = useCallback((changes: Partial<StorefrontUiSettings>) => {
        setResultsDraft(current => current ? ({
            ...current,
            uiSettings: mergeResultsAppearance(withUiDefaults(current.uiSettings), changes),
        }) : current);
    }, []);

    const updateAppearanceNumberDraft = useCallback((key: keyof StorefrontUiSettings, draft: AppearanceNumberDraft | undefined) => {
        setAppearanceNumberDrafts(current => {
            const next = { ...current };
            if (draft) next[key] = draft;
            else delete next[key];
            return next;
        });
    }, []);

    const updateRenderingMode = useCallback((mode: StorefrontRenderingMode) => {
        updateResultsDraft({ renderingMode: mode });
        if (mode !== "RenderedResults" && mode !== "AppProxyResults") return;
        setThemeDraft(current => {
            if (!current) return current;
            const nextPath = stripVehiclePlaceholderFromPath(current.redirectPath);
            return nextPath === current.redirectPath ? current : { ...current, redirectPath: nextPath };
        });
    }, [updateResultsDraft]);

    const sortedGroups = useMemo(
        () => (resultsDraft?.curatedGroups ?? [])
            .map((group, originalIndex) => ({ group, originalIndex }))
            .sort((left, right) =>
                left.group.displayOrder - right.group.displayOrder || left.group.name.localeCompare(right.group.name)),
        [resultsDraft?.curatedGroups]
    );

    const baselineTheme = themeSettings || DEFAULT_THEME_SETTINGS;
    const hasThemeChanges = !!themeDraft
        && themeSettingsSnapshot(themeDraft, persistentFilteringChecked, submodelEnabled)
            !== themeSettingsSnapshot(baselineTheme, !!settings?.searchEnablePersistentFiltering, settings?.submodelEnabled ?? false);
    const hasResultsChanges = resultSettingsSnapshot(resultsDraft) !== resultSettingsSnapshot(resultsBaseline);
    const hasChanges = hasThemeChanges || hasResultsChanges || Object.keys(appearanceNumberDrafts).length > 0;

    const tagSync = useFitmentTagSync(async () => {
        const saved = cloneResultsSettings(withResultsDefaults(await agent.Settings.storefrontResults()));
        setResultsDraft(current => current ? { ...current, renderingMode: saved.renderingMode,
            uiSettings: { ...current.uiSettings, keepShopifyFitmentTagsAligned: saved.uiSettings?.keepShopifyFitmentTagsAligned } } : saved);
        setResultsBaseline(cloneResultsSettings(saved));
        themeDraftAfterSync.current = Object.fromEntries(Object.entries(themeDraft || {}).filter(([key, value]) =>
            key !== "renderingMode" && key !== "redirectPath" && value !== (themeSettings as any)?.[key]));
        await settingStore.loadThemeSettings();
    });
    const startTagSync = tagSync.start;

    const save = useCallback(async () => {
        const updates: Promise<unknown>[] = [];
        let savedResults: StorefrontResultsSettings | undefined;
        // Shopify's save bar can run while an input still has focus. Include
        // its validated draft directly instead of relying on a blur event.
        const uiToSave = withUiDefaults(resultsDraft?.uiSettings);
        const pendingNumberChanges = Object.fromEntries(Object.entries(appearanceNumberDrafts)
            .flatMap(([key, draft]) => draft && draft.value !== uiToSave[key as keyof StorefrontUiSettings]
                ? [[key, draft.value]] : []));
        const resultsToSave = resultsDraft && {
            ...resultsDraft,
            uiSettings: mergeResultsAppearance(uiToSave, pendingNumberChanges),
        };
        // Search mode changes go through the health check, which applies them once confirmed.
        const modeChange = !!resultsDraft && (resultsDraft.renderingMode !== resultsBaseline?.renderingMode
            || !!resultsDraft.uiSettings?.keepShopifyFitmentTagsAligned !== !!resultsBaseline?.uiSettings?.keepShopifyFitmentTagsAligned);

        setSaving(true);
        setError(undefined);
        try {
            if (hasThemeChanges && themeDraft) {
                const tagPrefixEnabled = settings?.useYmmPrefixForProductTags ?? themeDraft.tagPrefixEnabled ?? true;
                const nextSetting: UserSetting = {
                    searchEnablePersistentFiltering: persistentFilteringChecked,
                    inventoryAutoMapEnabled: settings?.inventoryAutoMapEnabled ?? false,
                    useYmmPrefixForProductTags: tagPrefixEnabled,
                    submodelEnabled,
                    inventoryLocationIds: settings?.inventoryLocationIds ?? [],
                };
                if (settings?.id !== undefined) nextSetting.id = settings.id;

                updates.push(settingStore.updateSettings(nextSetting));
                updates.push(settingStore.updateThemeSettings({
                    ...themeDraft,
                    redirectPath: modeChange ? baselineTheme.redirectPath : themeDraft.redirectPath,
                    globalFilter: persistentFilteringChecked,
                    tagPrefixEnabled,
                    submodelEnabled,
                    renderingMode: modeChange ? resultsBaseline?.renderingMode ?? themeDraft.renderingMode : resultsDraft?.renderingMode ?? themeDraft.renderingMode,
                    resultsReturnType: resultsDraft?.returnResultsAs ?? themeDraft.resultsReturnType,
                    resultsGroupingMode: resultsDraft?.groupResultsBy ?? themeDraft.resultsGroupingMode,
                    priceDisplayMode: resultsDraft?.priceDisplayMode ?? themeDraft.priceDisplayMode,
                    variantResultDisplayMode: resultsDraft?.variantResultDisplayMode ?? themeDraft.variantResultDisplayMode,
                }));
            }

            if (resultsToSave && resultSettingsSnapshot(resultsToSave) !== resultSettingsSnapshot(resultsBaseline)) {
                updates.push(agent.Settings.updateStorefrontResults({
                    ...resultsToSave,
                    ...(modeChange ? { renderingMode: resultsBaseline?.renderingMode ?? "CollectionFiltering",
                        uiSettings: { ...resultsToSave.uiSettings, keepShopifyFitmentTagsAligned: !!resultsBaseline?.uiSettings?.keepShopifyFitmentTagsAligned } } : {}),
                    curatedGroups: sortedGroups.map(({ group }) => group),
                }).then(saved => { savedResults = saved; }));
            }

            const results = await Promise.all(updates);
            // The store reports failed updates as false instead of throwing.
            if (results.some(result => result === false)) throw new Error("Settings could not be saved. Try again.");
            setAppearanceNumberDrafts({});
            if (savedResults) {
                const next = cloneResultsSettings(withResultsDefaults(savedResults));
                setResultsDraft(next);
                setResultsBaseline(cloneResultsSettings(next));
            }
            window.shopify?.toast?.show?.("Settings saved");
            if (modeChange && resultsDraft) await startTagSync(resultsDraft.renderingMode, !!resultsDraft.uiSettings?.keepShopifyFitmentTagsAligned, themeDraft?.redirectPath, resultsBaseline?.renderingMode);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err?.message ?? "Failed to save storefront settings.");
            throw err;
        } finally {
            setSaving(false);
        }
    }, [appearanceNumberDrafts, baselineTheme.redirectPath, hasThemeChanges, persistentFilteringChecked, resultsBaseline,
        resultsDraft, settingStore, settings, sortedGroups, startTagSync, submodelEnabled, themeDraft]);

    const discard = useCallback(() => {
        setAppearanceNumberDrafts({});
        setPersistentFilteringChecked(!!settings?.searchEnablePersistentFiltering);
        setSubmodelEnabled(settings?.submodelEnabled ?? false);
        setThemeDraft(themeSettings);
        if (resultsBaseline) setResultsDraft(cloneResultsSettings(resultsBaseline));
        setError(undefined);
    }, [resultsBaseline, settings, themeSettings]);

    const loading = loadingInitial || loadingThemeSettings || !themeDraft || resultsLoading || !resultsDraft;
    return {
        loading, saving, error,
        settings, themeSettings, themeDraft, resultsDraft, resultsBaseline, sortedGroups,
        persistentFilteringChecked, setPersistentFilteringChecked,
        submodelEnabled, setSubmodelEnabled,
        appearanceNumberDrafts,
        updateThemeDraft, updateSearchDestinationMode, updateResultsPageLocationMode, updateResultsDraft,
        updateUiSettingsDraft, updateAppearanceNumberDraft, updateRenderingMode, setResultsDraft,
        tagSync,
        form: { dirty: hasChanges, saving, onSave: () => { void save().catch(() => undefined); }, onDiscard: discard },
    };
}
